import { useState, useEffect } from 'react';
import { ArrowRight, Loader2, Upload } from 'lucide-react';

export default function CareersPage() {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    trade: '',
    experience: '',
    message: '',
    resume: null,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/roles`);
        if (response.ok) {
          const data = await response.json();
          setRoles(data);
        }
      } catch (error) {
        console.error('Error fetching roles:', error);
      }
      setLoading(false);
    };
    fetchRoles();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('name', form.name);
      formData.append('email', form.email);
      formData.append('phone', form.phone);
      formData.append('trade', form.trade);
      formData.append('experience', form.experience);
      formData.append('message', form.message);
      if (form.resume) {
        formData.append('resume', form.resume);
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/careers/apply`, {
        method: 'POST',
        body: formData,
      });
      if (response.ok) {
        setSubmitted(true);
        setForm({ name: '', email: '', phone: '', trade: '', experience: '', message: '', resume: null });
      }
    } catch (error) {
      console.error('Error submitting application:', error);
    }
    setSubmitting(false);
  };

  return (
    <main className="pt-20" data-testid="careers-page">
      {/* Hero */}
      <section className="py-24 md:py-32 bg-[#3D3A38]">
        <div className="container-custom">
          <p className="section-label mb-4 text-[#F5F1EC]/60">Careers</p>
          <h1 
            className="text-4xl md:text-5xl lg:text-6xl text-[#F5F1EC] mb-6"
            style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 400 }}
          >
            Build your career with <span className="italic text-[#B8A066]">us.</span>
          </h1>
          <p 
            className="text-lg text-[#F5F1EC]/60 max-w-2xl"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            We're always looking for skilled electricians and professionals who share our commitment to quality work.
          </p>
        </div>
      </section>

      {/* Open Positions */}
      <section className="py-24 md:py-32 bg-[#F5F1EC]">
        <div className="container-custom">
          <div className="mb-16">
            <p className="section-label mb-4">Open Positions</p>
            <h2 
              className="text-3xl md:text-4xl text-[#3D3A38]"
              style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 400 }}
            >
              Current <span className="italic text-[#B8A066]">opportunities.</span>
            </h2>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="w-8 h-8 border-2 border-[#B8A066] border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
          ) : roles.length === 0 ? (
            <div className="p-8 border border-[#E5DFD5] text-center">
              <p className="text-[#5C5855]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                No open positions at the moment. However, we're always interested in hearing from skilled professionals.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {roles.map((role, index) => (
                <div 
                  key={index}
                  className="p-6 border border-[#E5DFD5] hover:border-[#B8A066] transition-colors duration-300"
                >
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                      <h3 
                        className="text-xl text-[#3D3A38] mb-1"
                        style={{ fontFamily: 'Cormorant Garamond, serif' }}
                      >
                        {role.title}
                      </h3>
                      <p 
                        className="text-sm text-[#5C5855]"
                        style={{ fontFamily: 'Outfit, sans-serif' }}
                      >
                        {role.location} · {role.type}
                      </p>
                    </div>
                    <a 
                      href="#apply"
                      className="text-xs tracking-[0.15em] uppercase text-[#3D3A38] hover:text-[#B8A066] transition-colors inline-flex items-center gap-2"
                      style={{ fontFamily: 'Outfit, sans-serif' }}
                    >
                      Apply Now <ArrowRight size={12} />
                    </a>
                  </div>
                  {role.description && (
                    <p 
                      className="text-sm text-[#5C5855] mt-4 leading-relaxed"
                      style={{ fontFamily: 'Outfit, sans-serif' }}
                    >
                      {role.description}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Application Form */}
      <section id="apply" className="py-24 md:py-32 bg-[#3D3A38]">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto">
            <div className="mb-12 text-center">
              <p className="section-label mb-4 text-[#F5F1EC]/60">Apply</p>
              <h2 
                className="text-3xl md:text-4xl text-[#F5F1EC]"
                style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 400 }}
              >
                Join our <span className="italic text-[#B8A066]">team.</span>
              </h2>
            </div>

            {submitted ? (
              <div className="text-center py-12">
                <h3 
                  className="text-2xl text-[#F5F1EC] mb-4"
                  style={{ fontFamily: 'Cormorant Garamond, serif' }}
                >
                  Thank you for your application.
                </h3>
                <p 
                  className="text-[#F5F1EC]/60 mb-8"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  We'll review your submission and get back to you soon.
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="btn-secondary border-[#F5F1EC] text-[#F5F1EC] hover:bg-[#F5F1EC] hover:text-[#3D3A38]"
                >
                  Submit another application
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <label className="form-label text-[#F5F1EC]/60">Full Name</label>
                    <input
                      type="text"
                      required
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="form-input-underline text-[#F5F1EC] border-[#F5F1EC]/20 focus:border-[#B8A066]"
                      placeholder="First and last"
                      style={{ background: 'transparent' }}
                      data-testid="career-name"
                    />
                  </div>
                  <div>
                    <label className="form-label text-[#F5F1EC]/60">Email</label>
                    <input
                      type="email"
                      required
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="form-input-underline text-[#F5F1EC] border-[#F5F1EC]/20 focus:border-[#B8A066]"
                      placeholder="name@email.com"
                      style={{ background: 'transparent' }}
                      data-testid="career-email"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <label className="form-label text-[#F5F1EC]/60">Phone</label>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className="form-input-underline text-[#F5F1EC] border-[#F5F1EC]/20 focus:border-[#B8A066]"
                      placeholder="+1"
                      style={{ background: 'transparent' }}
                      data-testid="career-phone"
                    />
                  </div>
                  <div>
                    <label className="form-label text-[#F5F1EC]/60">Trade / Position</label>
                    <input
                      type="text"
                      value={form.trade}
                      onChange={(e) => setForm({ ...form, trade: e.target.value })}
                      className="form-input-underline text-[#F5F1EC] border-[#F5F1EC]/20 focus:border-[#B8A066]"
                      placeholder="e.g., 309A Licensed Electrician"
                      style={{ background: 'transparent' }}
                      data-testid="career-trade"
                    />
                  </div>
                </div>

                <div>
                  <label className="form-label text-[#F5F1EC]/60">Years of Experience</label>
                  <select
                    value={form.experience}
                    onChange={(e) => setForm({ ...form, experience: e.target.value })}
                    className="form-input-underline text-[#F5F1EC] border-[#F5F1EC]/20 focus:border-[#B8A066] cursor-pointer"
                    style={{ background: 'transparent' }}
                    data-testid="career-experience"
                  >
                    <option value="" className="text-[#3D3A38]">Select</option>
                    <option value="0-2 years" className="text-[#3D3A38]">0-2 years</option>
                    <option value="3-5 years" className="text-[#3D3A38]">3-5 years</option>
                    <option value="5-10 years" className="text-[#3D3A38]">5-10 years</option>
                    <option value="10+ years" className="text-[#3D3A38]">10+ years</option>
                  </select>
                </div>

                <div>
                  <label className="form-label text-[#F5F1EC]/60">Tell us about yourself</label>
                  <textarea
                    rows={4}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="form-input-underline text-[#F5F1EC] border-[#F5F1EC]/20 focus:border-[#B8A066] resize-none"
                    placeholder="Your experience, certifications, and what you're looking for."
                    style={{ background: 'transparent' }}
                    data-testid="career-message"
                  />
                </div>

                <div>
                  <label className="form-label text-[#F5F1EC]/60">Resume (PDF)</label>
                  <div className="relative">
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx"
                      onChange={(e) => setForm({ ...form, resume: e.target.files[0] })}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      data-testid="career-resume"
                    />
                    <div className="form-input-underline text-[#F5F1EC]/50 border-[#F5F1EC]/20 flex items-center gap-2 cursor-pointer">
                      <Upload size={16} />
                      {form.resume ? form.resume.name : 'Click to upload'}
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary w-full inline-flex items-center justify-center gap-2"
                  data-testid="career-submit"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={14} className="animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      Submit Application <ArrowRight size={14} />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </main>
  );
}
